/**
 * @description Dialog de autenticação para agendamento de consultas
 * @module AppointmentAuthDialog
 * @since Dezembro 2024
 * @remarks Dialog que verifica se o usuário está cadastrado e exibe formulário de login ou signup completo
 */

import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  Typography,
  Box,
  CircularProgress,
  Alert,
  IconButton,
  InputAdornment,
  useTheme,
  useMediaQuery,
  Grid,
  MenuItem,
  Switch,
  FormControlLabel,
} from "@mui/material";
import {
  Close as CloseIcon,
  Visibility,
  VisibilityOff,
  Lock as LockIcon,
  Badge as BadgeIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Wc as GenderIcon,
  WhatsApp as WhatsAppIcon,
  AttachMoney as AttachMoneyIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
} from "@mui/icons-material";
import { useForm, Controller } from "react-hook-form";
import { toast } from "react-toastify";
import { useFormatter } from "@/utils/useFormatter";
import { useRegex } from "@/utils/useRegex";
import { useValidate } from "@/utils/useValidate";
import { currentFeatures } from "@/configs/Settings";
import { SearchCPF } from "@/services/scim/signup/SearchCPF";
import { SignUpRegister } from "@/services/scim/signup/SignUpRegister";
import { PutPerson } from "@/services/fhir/person/PutPerson";
import { GetPatient } from "@/services/fhir";
import { OAuthUser } from "@/services/auth";
import { useFirebaseService } from "@/utils/useFirebase";
import { HTTPResponseStatus } from "@/utils/enum";
import dayjs from "dayjs";
import { AppointmentVetPetSelector } from "@/components/modals/AppointmentVetPetSelector";
import { ITutorData } from "@/services/fhir/vetpatient/CreateVetPatientWithRelation";
import { ProfileSearch } from "@/services/scim";
import {
  setRefreshToken,
  setUserAccessToken,
  setUserAccessTokenExpiresAt,
} from "src-next/shared/api/utils/tokenStorage";

/**
 * @interface AppointmentAuthDialogProps
 * @description Props do dialog de autenticação
 */
interface AppointmentAuthDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  /**
   * Callback opcional para modo veterinário (não-defaultApp).
   * Retorna o ID do pet selecionado antes de confirmar o fluxo.
   */
  onVetPetSelected?: (petId: string) => void;
  slotInfo?: {
    date: string;
    time: string;
    practitionerName: string;
    serviceCost?: number; // Preço da consulta (opcional)
  };
}

/**
 * @interface LoginFormData
 * @description Dados do formulário de login
 */
interface LoginFormData {
  password: string;
}

/**
 * @interface SignupFormData
 * @description Dados do formulário de signup
 */
interface SignupFormData {
  cpf: string;
  phoneNumber: string;
  email: string;
  gender: string;
  confirmationToken: string;
  password: string;
  confirmPassword: string;
  notification: boolean;
}

/**
 * @interface ICPFValidationData
 * @description Dados do CPF validado
 */
interface ICPFValidationData {
  ni: string;
  nome: string;
  situacao: {
    codigo: string;
    descricao: string;
  };
  nascimento: string;
}

/**
 * @enum AuthStep
 * @description Etapas do fluxo de autenticação
 */
enum AuthStep {
  CPF_INPUT = "cpf_input",
  CHECKING = "checking",
  LOGIN = "login",
  SIGNUP_PERSONAL_DATA = "signup_personal_data",
  SIGNUP_TOKEN = "signup_token",
  SIGNUP_PASSWORD = "signup_password",
  VET_PET_SELECT = "vet_pet_select",
}

/**
 * @component AppointmentAuthDialog
 * @description Dialog de autenticação para agendamento
 */
export function AppointmentAuthDialog({
  open,
  onClose,
  onSuccess,
  onVetPetSelected,
  slotInfo,
}: AppointmentAuthDialogProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Estado do fluxo de autenticação
  const [authStep, setAuthStep] = useState<AuthStep>(AuthStep.CPF_INPUT);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [userCpf, setUserCpf] = useState<string>("");
  const [initialCpf, setInitialCpf] = useState<string>("");
  const [cpfValidationData, setCpfValidationData] =
    useState<ICPFValidationData | null>(null);
  const [openTokenDialog, setOpenTokenDialog] = useState(false);
  const [tokenSent, setTokenSent] = useState(false);
  const [timer, setTimer] = useState(30);
  const [canResend, setCanResend] = useState(false);
  const [vetTutorData, setVetTutorData] = useState<ITutorData | null>(null);

  // Formulário de login (apenas senha)
  const {
    control: loginControl,
    handleSubmit: handleLoginSubmit,
    watch: watchLogin,
    formState: { errors: loginErrors },
    reset: resetLogin,
  } = useForm<LoginFormData>({
    mode: "onSubmit",
    defaultValues: {
      password: "",
    },
  });

  // Formulário de signup
  const {
    control: signupControl,
    handleSubmit: handleSignupSubmit,
    watch: watchSignup,
    formState: { errors: signupErrors },
    reset: resetSignup,
  } = useForm<SignupFormData>({
    mode: "onSubmit",
    defaultValues: {
      cpf: "",
      phoneNumber: "",
      email: "",
      gender: "",
      confirmationToken: "",
      password: "",
      confirmPassword: "",
      notification: true,
    },
  });

  const watchedSignupPhone = watchSignup("phoneNumber");
  const watchedSignupEmail = watchSignup("email");
  const watchedSignupGender = watchSignup("gender");
  const watchedSignupPassword = watchSignup("password");
  const watchedSignupConfirmPassword = watchSignup("confirmPassword");

  /**
   * @description Formata a data de nascimento do formato DDMMYYYY para DD/MM/YYYY
   */
  const formatBirthDate = (dateString: string): string => {
    if (!dateString || dateString.length !== 8) return dateString;
    const day = dateString.substring(0, 2);
    const month = dateString.substring(2, 4);
    const year = dateString.substring(4, 8);
    return `${day}/${month}/${year}`;
  };

  /**
   * @description Reseta o estado do dialog quando fechado
   */
  useEffect(() => {
    if (!open) {
      setAuthStep(AuthStep.CPF_INPUT);
      setUserCpf("");
      setInitialCpf("");
      setCpfValidationData(null);
      setShowPassword(false);
      setShowConfirmPassword(false);
      setOpenTokenDialog(false);
      setTokenSent(false);
      setTimer(30);
      setCanResend(false);
      setVetTutorData(null);
      resetLogin();
      resetSignup();
    }
  }, [open, resetLogin, resetSignup]);

  /**
   * @description Timer para reenvio de código
   */
  useEffect(() => {
    let interval: NodeJS.Timeout;

    if (openTokenDialog && timer > 0) {
      interval = setInterval(() => {
        setTimer((prev) => {
          if (prev <= 1) {
            setCanResend(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [openTokenDialog, timer]);

  /**
   * @description Valida CPF antes de verificar cadastro
   */
  const validateAndCheckCpf = () => {
    if (!initialCpf) {
      toast.error("CPF é obrigatório");
      return;
    }

    if (!useValidate.cpfNumber(initialCpf)) {
      toast.error("CPF inválido");
      return;
    }

    checkUserRegistration(initialCpf);
  };

  /**
   * @description Verifica se o CPF está cadastrado no sistema usando GetPatient (FHIR)
   * Se encontrar, autentica sem senha e prossegue para seleção de pet (vet) ou sucesso (humano)
   * Se não encontrar, busca automaticamente no Serpro via SearchCPF e preenche os dados
   */
  const checkUserRegistration = async (cpf: string) => {
    setLoading(true);
    setAuthStep(AuthStep.CHECKING);

    try {
      const cleanCPF = cpf.replace(/\D/g, "");
      setUserCpf(cleanCPF);

      // Verificar se o paciente está cadastrado usando GetPatient (FHIR)
      const patientResponse = await GetPatient({
        authType: "application",
        identifier: cleanCPF,
        vetPatient: !currentFeatures.defaultApp, // Se vet, adiciona header X-Target-System
      });

      // Se encontrou o paciente no FHIR, autenticar sem senha e prosseguir
      if (patientResponse && patientResponse.data) {
        const patient = patientResponse.data;
        const patientName = patient.name?.[0]?.text || "";

        // Salvar CPF no localStorage para uso posterior (simula autenticação)
        localStorage.setItem("username", cleanCPF);
        localStorage.setItem("logged", "true");

        // toast.success("Paciente identificado com sucesso!");

        // No modo vet (não-defaultApp), após identificar precisamos selecionar o pet
        if (!currentFeatures.defaultApp) {
          const tutorPhone =
            patient.telecom?.find((t: any) => t.system === "phone")?.value ||
            "";
          const tutorGender = patient.gender || "";

          setVetTutorData({
            cpf: cleanCPF,
            name: patientName,
            phoneNumber: tutorPhone,
            gender: tutorGender,
          });
          setAuthStep(AuthStep.VET_PET_SELECT);
        } else {
          // Modo humano - prosseguir direto para sucesso
          onSuccess();
          onClose();
        }
      } else {
        // Não encontrou paciente no FHIR - buscar automaticamente no Serpro
        console.log(
          "Paciente não encontrado no FHIR, buscando dados no Serpro...",
        );

        try {
          // Buscar dados do CPF no Serpro
          const serproResponse = await SearchCPF(cleanCPF, "application");

          // Verificar se a resposta foi bem-sucedida
          if (
            serproResponse &&
            serproResponse.status === HTTPResponseStatus.Success &&
            serproResponse.data
          ) {
            // Armazenar os dados do CPF validado para uso no registro
            const validationData = serproResponse.data as ICPFValidationData;
            setCpfValidationData(validationData);

            console.log("Dados do CPF obtidos do Serpro:", validationData);
            toast.success("Dados do CPF validados com sucesso!");
          } else if (
            serproResponse &&
            serproResponse.status === HTTPResponseStatus.UnprocessableEntity
          ) {
            // CPF inválido ou menor de idade
            toast.warn(
              "Não foi possível validar o CPF ou é menor de idade. Por favor, tente novamente.",
            );
            setLoading(false);
            setAuthStep(AuthStep.CPF_INPUT);
            return;
          } else {
            // Resposta inesperada do Serpro
            console.warn("Resposta inesperada do Serpro:", serproResponse);
            toast.warn(
              "Não foi possível validar o CPF automaticamente. Você pode continuar o cadastro.",
            );
          }
        } catch (serproError) {
          // Erro ao buscar no Serpro - continuar mesmo assim
          console.error("Erro ao buscar CPF no Serpro:", serproError);
          toast.warn(
            "Não foi possível validar o CPF automaticamente. Você pode continuar o cadastro.",
          );
        }

        // Iniciar signup (com ou sem dados do Serpro)
        setAuthStep(AuthStep.SIGNUP_PERSONAL_DATA);
        resetSignup({
          cpf: cpf,
          phoneNumber: "",
          email: "",
          gender: "",
          confirmationToken: "",
          password: "",
          confirmPassword: "",
          notification: true,
        });
      }
    } catch (error) {
      console.error("Erro ao verificar cadastro:", error);

      // Em caso de erro, tentar buscar no Serpro antes de iniciar signup
      try {
        const cleanCPF = cpf.replace(/\D/g, "");
        console.log("Tentando buscar CPF no Serpro após erro...");

        const serproResponse = await SearchCPF(cleanCPF, "application");

        if (
          serproResponse &&
          serproResponse.status === HTTPResponseStatus.Success &&
          serproResponse.data
        ) {
          const validationData = serproResponse.data as ICPFValidationData;
          setCpfValidationData(validationData);
          console.log(
            "Dados do CPF obtidos do Serpro após erro:",
            validationData,
          );
          toast.success("Dados do CPF validados com sucesso!");
        }
      } catch (serproError) {
        console.error("Erro ao buscar CPF no Serpro após erro:", serproError);
      }

      // Iniciar signup (com ou sem dados do Serpro)
      setAuthStep(AuthStep.SIGNUP_PERSONAL_DATA);
      resetSignup({
        cpf: cpf,
        phoneNumber: "",
        email: "",
        gender: "",
        confirmationToken: "",
        password: "",
        confirmPassword: "",
        notification: true,
      });
    } finally {
      setLoading(false);
    }
  };

  // Removido handleLogin - agora usamos autenticação sem senha via GetPatient

  /**
   * @description Valida CPF do usuário antes de prosseguir com signup
   */
  const validateUserCPF = async (cpf: string) => {
    try {
      const cleanCPF = cpf.replace(/[^0-9]/g, "");

      // Verificar se o CPF já está cadastrado no SCIM
      const Scim2UserResponse = await ProfileSearch(
        "application",
        cleanCPF,
      ).then((response) => response?.Resources);

      // Se o usuário existe no SCIM, não pode cadastrar novamente
      if (Scim2UserResponse && Scim2UserResponse.length > 0) {
        toast.warn("CPF já cadastrado no sistema. Tente fazer login.");
        return false;
      }

      // Se não existe no SCIM, verificar se existe como Patient no FHIR
      try {
        const patientResponse = await GetPatient({
          authType: "application",
          identifier: cleanCPF,
        });

        // Se encontrou o paciente no FHIR, usar os dados dele
        if (patientResponse && patientResponse.data) {
          const patient = patientResponse.data;
          const patientName = patient.name?.[0]?.text || "";
          const patientBirthDate = patient.birthDate || "";

          // Formatar data de nascimento do formato YYYY-MM-DD para DDMMYYYY
          let formattedBirthDate = "";
          if (patientBirthDate) {
            const [year, month, day] = patientBirthDate.split("-");
            formattedBirthDate = `${day}${month}${year}`;
          }

          // Criar objeto no formato esperado
          const validationData: ICPFValidationData = {
            ni: cleanCPF,
            nome: patientName,
            situacao: {
              codigo: "0",
              descricao: "Regular",
            },
            nascimento: formattedBirthDate,
          };

          setCpfValidationData(validationData);
          return true;
        }
      } catch (fhirError) {
        // Se não encontrou no FHIR, continuar para validação no Serpro
        console.log("Paciente não encontrado no FHIR, validando no Serpro...");
      }

      // Se não existe no FHIR, validar CPF no Serpro
      try {
        const serproResponse = await SearchCPF(cleanCPF, "application");

        // Se o CPF estiver em uso no SCIM (status 422)
        if (
          serproResponse &&
          serproResponse.status === HTTPResponseStatus.UnprocessableEntity
        ) {
          toast.warn(
            "Não foi possível validar o CPF ou é menor de idade. Por favor, tente novamente.",
          );
          return false;
        }

        // CPF válido mas não cadastrado, armazenar dados para registro posterior
        if (serproResponse && serproResponse.data) {
          const validationData = serproResponse.data as ICPFValidationData;
          setCpfValidationData(validationData);
        }

        return true;
      } catch (serproError) {
        console.error("Erro ao validar CPF no Serpro:", serproError);
        toast.error("Erro ao validar CPF. Verifique se o CPF é válido.");
        return false;
      }
    } catch (error) {
      console.error("Erro ao validar CPF:", error);
      toast.error("Erro ao validar CPF. Tente novamente.");
      return false;
    }
  };

  /**
   * @description Envia código de confirmação para o telefone
   */
  const handleSendToken = async () => {
    // Verificar campos obrigatórios
    const signupData = watchSignup();
    if (
      !signupData.phoneNumber ||
      !useRegex.phoneNumber.test(signupData.phoneNumber) ||
      !signupData.email ||
      !useRegex.email.test(signupData.email) ||
      !signupData.gender
    ) {
      toast.error("Preencha todos os campos obrigatórios corretamente");
      return;
    }

    setLoading(true);
    try {
      // Validar CPF antes de prosseguir
      const canProceed = await validateUserCPF(signupData.cpf);
      if (!canProceed) {
        setLoading(false);
        return;
      }

      // Limpar token anterior
      resetSignup((formValues) => ({
        ...formValues,
        confirmationToken: "",
      }));

      // Integração com Firebase para enviar token
      await useFirebaseService.sendVerificationCode(watchedSignupPhone);
      toast.success(`Código enviado para ${watchedSignupPhone}`);
      setTokenSent(true);
      setOpenTokenDialog(true);
      setTimer(30);
      setCanResend(false);
    } catch (error) {
      console.error("Erro ao enviar token:", error);
      toast.error("Erro ao enviar código de verificação");
    } finally {
      setLoading(false);
    }
  };

  /**
   * @description Reenvia o código de confirmação
   */
  const handleResendToken = async () => {
    setLoading(true);
    try {
      await useFirebaseService.sendVerificationCode(watchedSignupPhone);
      toast.info("Código reenviado com sucesso!");
      setTimer(30);
      setCanResend(false);
    } catch (error) {
      console.error("Erro ao reenviar token:", error);
      toast.error("Erro ao reenviar código de verificação");
    } finally {
      setLoading(false);
    }
  };

  /**
   * @description Valida e confirma o token
   */
  const handleVerifyToken = async () => {
    const token = watchSignup("confirmationToken");
    if (token && token.length === 6) {
      setLoading(true);
      try {
        // Integração com Firebase para verificar token
        await useFirebaseService.verifyCode(token);
        toast.success("Código validado com sucesso!");
        setOpenTokenDialog(false);
        setAuthStep(AuthStep.SIGNUP_PASSWORD);
      } catch (error) {
        console.error("Erro ao verificar token:", error);
        toast.error("Código inválido. Tente novamente.");
      } finally {
        setLoading(false);
      }
    } else {
      toast.warn("Digite o código completo de 6 dígitos");
    }
  };

  /**
   * @description Registra o usuário no sistema
   */
  const registerUser = async (data: SignupFormData) => {
    try {
      // Preparar os dados para registro
      const registerData = {
        firstName: cpfValidationData?.nome.split(" ")[0] || "",
        surname: cpfValidationData?.nome.split(" ").slice(1).join(" ") || "",
        identifier: data.cpf.replace(/[^0-9]/g, ""),
        birthdate: dayjs(cpfValidationData?.nascimento, "DDMMYYYY"),
        instagram: "",
        password: data.password,
        passwordConfirm: data.confirmPassword,
        phoneNumber: `+55${data.phoneNumber}`,
        email: data.email,
        gender: data.gender,
        firebaseCode: data.confirmationToken || "",
        firebaseToken: "123456",
        notification: data.notification,
      };

      // Chamar a API para registrar o usuário
      const response = await SignUpRegister(registerData);
      const dataPerson = {
        identifier: data.cpf.replace(/[^0-9]/g, ""),
        name: cpfValidationData?.nome,
        birthDate: dayjs(cpfValidationData?.nascimento, "DDMMYYYY"),
        active: true,
      };
      await PutPerson(dataPerson, true);

      if (
        response &&
        (response.status === HTTPResponseStatus.Created ||
          response.status === HTTPResponseStatus.Conflict)
      ) {
        if (response.status === HTTPResponseStatus.Created) {
          toast.success("Usuário cadastrado com sucesso!");
        } else {
          toast.warn("Usuário já existe no sistema.");
        }

        return true;
      } else {
        toast.error("Erro ao cadastrar usuário. Tente novamente.");
        return false;
      }
    } catch (error) {
      console.error("Erro ao registrar usuário:", error);
      toast.error("Erro ao cadastrar usuário. Tente novamente.");
      return false;
    }
  };

  /**
   * @description Finaliza o signup e realiza login automático
   */
  const handleCompleteSignup = async (data: SignupFormData) => {
    setLoading(true);
    try {
      // Registrar o usuário
      if (cpfValidationData) {
        const registered = await registerUser(data);
        if (!registered) {
          throw new Error("Falha ao registrar usuário");
        }
      }

      // Realizar login automático após cadastro bem-sucedido
      const cleanCPF = data.cpf.replace(/\D/g, "");

      try {
        const authResponse = await OAuthUser(cleanCPF, data.password);

        if (authResponse) {
          setUserAccessToken(authResponse.access_token);
          localStorage.setItem("logged", "true");
          localStorage.setItem("username", cleanCPF);
          setRefreshToken(authResponse.refresh_token);
          localStorage.setItem("access", JSON.stringify({ type: "patient" }));

          setUserAccessTokenExpiresAt(
            Date.now() + authResponse.expires_in * 1000,
          );

          toast.success("Cadastro e login realizados com sucesso!");
          // No modo vet (não-defaultApp), após cadastrar/logar precisamos selecionar o pet.
          if (!currentFeatures.defaultApp) {
            setVetTutorData({
              cpf: cleanCPF,
              name: cpfValidationData?.nome || "",
              phoneNumber: data.phoneNumber || "",
              gender: data.gender || "",
            });
            setAuthStep(AuthStep.VET_PET_SELECT);
            return;
          }

          onSuccess();
          onClose();
        } else {
          throw new Error("Falha no login automático");
        }
      } catch (loginError) {
        console.error("Erro ao fazer login automático:", loginError);
        toast.info(
          "Cadastro realizado! Por favor, faça login com suas credenciais.",
        );
        onClose();
      }
    } catch (error) {
      console.error("Erro ao finalizar cadastro:", error);
      toast.error("Erro ao finalizar cadastro. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  /**
   * @description Volta para a etapa anterior
   */
  const handleBack = () => {
    if (authStep === AuthStep.SIGNUP_PASSWORD) {
      setAuthStep(AuthStep.SIGNUP_PERSONAL_DATA);
    } else if (authStep === AuthStep.SIGNUP_PERSONAL_DATA) {
      setAuthStep(AuthStep.CPF_INPUT);
      setUserCpf("");
      setCpfValidationData(null);
      resetSignup();
    } else if (authStep === AuthStep.VET_PET_SELECT) {
      // Voltar para o início do fluxo
      setAuthStep(AuthStep.CPF_INPUT);
      setVetTutorData(null);
    }
  };

  /**
   * @description Renderiza a etapa de entrada de CPF
   */
  const renderCpfInput = () => (
    <Stack spacing={3}>
      <Box sx={{ textAlign: "center", mb: 2 }}>
        {slotInfo && (
          <>
            <Typography variant="body2" color="text.secondary">
              {slotInfo.practitionerName} - {slotInfo.date} às {slotInfo.time}
            </Typography>
            {/* Exibe o preço da consulta se disponível */}
            {slotInfo.serviceCost !== undefined && (
              <Box
                sx={{
                  mt: 2,
                  p: 2,
                  borderRadius: 2,
                  bgcolor: "primary",
                  border: "2px solid",
                  borderColor: "primary.main",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 1,
                }}
              >
                <AttachMoneyIcon sx={{ color: "primary.main" }} />
                <Typography
                  variant="h5"
                  color="primary"
                  fontWeight={700}
                  component="span"
                >
                  R$ {slotInfo.serviceCost.toFixed(2)}
                </Typography>
              </Box>
            )}
          </>
        )}
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Para continuar, informe seu CPF
        </Typography>
      </Box>

      <TextField
        fullWidth
        label="CPF"
        value={initialCpf}
        onChange={(e) => setInitialCpf(useFormatter.formatCPF(e.target.value))}
        placeholder="000.000.000-00"
        inputProps={{ maxLength: 14 }}
        autoFocus
        onKeyPress={(e) => {
          if (e.key === "Enter" && !loading && initialCpf) {
            validateAndCheckCpf();
          }
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <BadgeIcon sx={{ color: "action.active" }} />
            </InputAdornment>
          ),
        }}
      />

      <Button
        fullWidth
        variant="contained"
        onClick={validateAndCheckCpf}
        disabled={loading || !initialCpf}
        sx={{
          bgcolor: "#00A884",
          py: 1.5,
          "&:hover": {
            bgcolor: "#008c6e",
          },
        }}
      >
        {loading ? "Verificando..." : "Continuar"}
      </Button>
    </Stack>
  );

  /**
   * @description Renderiza a etapa de verificação
   */
  const renderChecking = () => (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        py: 4,
        gap: 2,
      }}
    >
      <CircularProgress size={40} />
      <Typography color="text.secondary">Verificando cadastro...</Typography>
    </Box>
  );

  // Removido renderLogin - agora usamos autenticação sem senha via GetPatient

  /**
   * @description Renderiza o formulário de dados pessoais do signup
   */
  const renderSignupPersonalData = () => (
    <Stack spacing={3}>
      <Box sx={{ textAlign: "center", mb: 1 }}>
        <Typography variant="h6" fontWeight={600} gutterBottom>
          Criar uma conta
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Preencha seus dados para criar sua conta
        </Typography>
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Controller
            name="cpf"
            control={signupControl}
            render={({ field: { onChange, value } }) => (
              <TextField
                fullWidth
                label="CPF"
                value={value}
                onChange={(e) =>
                  onChange(useFormatter.formatCPF(e.target.value))
                }
                placeholder="000.000.000-00"
                inputProps={{ maxLength: 14 }}
                disabled
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <BadgeIcon sx={{ color: "action.active" }} />
                    </InputAdornment>
                  ),
                }}
              />
            )}
          />
        </Grid>

        {/* Campo de Nome - preenchido automaticamente e bloqueado */}
        {cpfValidationData && (
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Nome Completo"
              value={cpfValidationData.nome || ""}
              disabled
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonIcon sx={{ color: "action.active" }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiInputBase-root": {
                  bgcolor: "rgba(0, 0, 0, 0.02)",
                },
              }}
            />
          </Grid>
        )}

        {/* Campo de Data de Nascimento - preenchido automaticamente e bloqueado */}
        {cpfValidationData && cpfValidationData.nascimento && (
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Data de Nascimento"
              value={formatBirthDate(cpfValidationData.nascimento)}
              disabled
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <CalendarIcon sx={{ color: "action.active" }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiInputBase-root": {
                  bgcolor: "rgba(0, 0, 0, 0.02)",
                },
              }}
            />
          </Grid>
        )}

        <Grid item xs={12} sm={cpfValidationData?.nascimento ? 6 : 12}>
          <Controller
            name="gender"
            control={signupControl}
            rules={{
              required: "Sexo de nascimento é obrigatório",
            }}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                select
                label="Sexo de Nascimento"
                error={!!signupErrors.gender}
                helperText={signupErrors.gender?.message}
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <GenderIcon sx={{ color: "action.active" }} />
                    </InputAdornment>
                  ),
                }}
              >
                <MenuItem value="male">Masculino</MenuItem>
                <MenuItem value="female">Feminino</MenuItem>
              </TextField>
            )}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <Controller
            name="phoneNumber"
            control={signupControl}
            rules={{
              required: "Telefone é obrigatório",
              pattern: {
                value: useRegex.phoneNumber,
                message: "Telefone inválido",
              },
            }}
            render={({ field: { onChange, value } }) => (
              <TextField
                fullWidth
                label="Telefone Celular"
                value={value}
                onChange={(e) =>
                  onChange(useFormatter.formatPhoneNumber(e.target.value))
                }
                error={!!signupErrors.phoneNumber}
                helperText={signupErrors.phoneNumber?.message}
                placeholder="(00) 00000-0000"
                inputProps={{ maxLength: 15 }}
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PhoneIcon sx={{ color: "action.active" }} />
                    </InputAdornment>
                  ),
                }}
              />
            )}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <Controller
            name="email"
            control={signupControl}
            rules={{
              required: "E-mail é obrigatório",
              pattern: {
                value: useRegex.email,
                message: "E-mail inválido",
              },
            }}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label="E-mail"
                error={!!signupErrors.email}
                helperText={signupErrors.email?.message}
                placeholder="exemplo@email.com"
                type="email"
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon sx={{ color: "action.active" }} />
                    </InputAdornment>
                  ),
                }}
              />
            )}
          />
        </Grid>

        <Grid item xs={12}>
          <Controller
            name="notification"
            control={signupControl}
            defaultValue={true}
            render={({ field }) => (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  p: 2,
                  border: "2px solid",
                  borderColor: "#00A884",
                  borderRadius: 2,
                  bgcolor: "rgba(0, 168, 132, 0.05)",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 40,
                      height: 40,
                      borderRadius: 1.5,
                      bgcolor: "#25D366",
                      color: "white",
                    }}
                  >
                    <WhatsAppIcon sx={{ fontSize: 24 }} />
                  </Box>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      Notificações por WhatsApp
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Receba atualizações importantes
                    </Typography>
                  </Box>
                </Box>
                <Switch
                  {...field}
                  checked={field.value}
                  onChange={(e) => field.onChange(e.target.checked)}
                />
              </Box>
            )}
          />
        </Grid>
      </Grid>

      <Stack direction="row" spacing={1}>
        <Button
          variant="outlined"
          onClick={handleBack}
          disabled={loading}
          sx={{
            borderColor: "#00A884",
            color: "#00A884",
            "&:hover": {
              borderColor: "#008c6e",
              bgcolor: "rgba(0, 168, 132, 0.04)",
            },
          }}
        >
          Voltar
        </Button>
        <Button
          variant="contained"
          onClick={handleSendToken}
          disabled={loading}
          fullWidth
          sx={{
            bgcolor: "#00A884",
            "&:hover": {
              bgcolor: "#008c6e",
            },
          }}
        >
          {loading ? "Enviando..." : "Enviar Código de Verificação"}
        </Button>
      </Stack>
    </Stack>
  );

  /**
   * @description Renderiza o dialog de verificação de token
   */
  const renderTokenDialog = () => (
    <Dialog
      open={openTokenDialog}
      onClose={(_, reason) => {
        if (reason !== "backdropClick") {
          setOpenTokenDialog(false);
        }
      }}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Typography variant="h6">Verificação de Telefone</Typography>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={3}>
          <Alert severity="info">
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Digite o código de 6 dígitos enviado para:
            </Typography>
            <Typography variant="body1" fontWeight="medium">
              {watchedSignupPhone}
            </Typography>
          </Alert>

          <Controller
            name="confirmationToken"
            control={signupControl}
            rules={{
              required: "Código é obrigatório",
              minLength: {
                value: 6,
                message: "Código deve ter 6 dígitos",
              },
            }}
            render={({ field: { onChange, value } }) => (
              <TextField
                fullWidth
                label="Código de Verificação"
                value={value}
                onChange={(e) => {
                  const val = e.target.value.replace(/\D/g, "").slice(0, 6);
                  onChange(val);
                }}
                placeholder="000000"
                inputProps={{ maxLength: 6 }}
                autoFocus
                error={!!signupErrors.confirmationToken}
                helperText={signupErrors.confirmationToken?.message}
              />
            )}
          />
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 0 }}>
        <Stack direction="column" spacing={1} sx={{ width: "100%" }}>
          <Button
            fullWidth
            variant="contained"
            onClick={handleVerifyToken}
            disabled={
              !watchedSignupPhone ||
              !watchSignup("confirmationToken") ||
              watchSignup("confirmationToken").length < 6 ||
              loading
            }
            sx={{
              bgcolor: "#00A884",
              "&:hover": { bgcolor: "#008c6e" },
            }}
          >
            {loading ? "Verificando..." : "Verificar Código"}
          </Button>

          <Button
            fullWidth
            variant="outlined"
            onClick={handleResendToken}
            disabled={!canResend || loading}
            sx={{
              borderColor: "#00A884",
              color: "#00A884",
              "&:hover": {
                borderColor: "#008c6e",
                bgcolor: "rgba(0, 168, 132, 0.04)",
              },
            }}
          >
            {loading
              ? "Enviando..."
              : canResend
                ? "Reenviar código"
                : `Aguarde ${timer}s para reenviar`}
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );

  /**
   * @description Renderiza o formulário de criação de senha
   */
  const renderSignupPassword = () => (
    <Stack spacing={3}>
      <Box sx={{ textAlign: "center", mb: 1 }}>
        <Typography variant="h6" fontWeight={600} gutterBottom>
          Criar Senha de Acesso
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Defina uma senha segura para acessar a plataforma
        </Typography>
      </Box>

      <Controller
        name="password"
        control={signupControl}
        rules={{
          required: "Senha é obrigatória",
          minLength: {
            value: 5,
            message: "A senha deve ter no mínimo 5 caracteres",
          },
        }}
        render={({ field }) => (
          <TextField
            {...field}
            fullWidth
            type={showPassword ? "text" : "password"}
            label="Senha"
            error={!!signupErrors.password}
            helperText={signupErrors.password?.message}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockIcon sx={{ color: "action.active" }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        )}
      />

      <Controller
        name="confirmPassword"
        control={signupControl}
        rules={{
          required: "Confirmação de senha é obrigatória",
          validate: (value) =>
            value === watchedSignupPassword || "As senhas não coincidem",
        }}
        render={({ field }) => (
          <TextField
            {...field}
            fullWidth
            type={showConfirmPassword ? "text" : "password"}
            label="Confirmar Senha"
            error={!!signupErrors.confirmPassword}
            helperText={signupErrors.confirmPassword?.message}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockIcon sx={{ color: "action.active" }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    edge="end"
                  >
                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        )}
      />

      <Stack direction="row" spacing={1}>
        <Button
          variant="outlined"
          onClick={handleBack}
          disabled={loading}
          sx={{
            borderColor: "#00A884",
            color: "#00A884",
            "&:hover": {
              borderColor: "#008c6e",
              bgcolor: "rgba(0, 168, 132, 0.04)",
            },
          }}
        >
          Voltar
        </Button>
        <Button
          variant="contained"
          onClick={handleSignupSubmit(handleCompleteSignup)}
          disabled={loading}
          fullWidth
          sx={{
            bgcolor: "#00A884",
            "&:hover": {
              bgcolor: "#008c6e",
            },
          }}
        >
          {loading ? "Finalizando..." : "Finalizar Cadastro"}
        </Button>
      </Stack>
    </Stack>
  );

  /**
   * @description Renderiza o conteúdo baseado na etapa atual
   */
  const renderContent = () => {
    switch (authStep) {
      case AuthStep.CPF_INPUT:
        return renderCpfInput();
      case AuthStep.CHECKING:
        return renderChecking();
      case AuthStep.SIGNUP_PERSONAL_DATA:
        return renderSignupPersonalData();
      case AuthStep.SIGNUP_PASSWORD:
        return renderSignupPassword();
      case AuthStep.VET_PET_SELECT:
        return vetTutorData ? (
          <AppointmentVetPetSelector
            tutorCpf={userCpf}
            tutorData={vetTutorData}
            onBack={handleBack}
            onConfirm={(petId) => {
              onVetPetSelected?.(petId);
              onSuccess();
              onClose();
            }}
          />
        ) : (
          <Alert severity="warning">
            Não foi possível carregar os dados do tutor para listar/cadastrar
            pets. Volte e tente novamente.
          </Alert>
        );
      default:
        return null;
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : 3,
          },
        }}
      >
        <DialogTitle>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography variant="h6" fontWeight={600}>
              {authStep === AuthStep.CPF_INPUT
                ? "Confirmar Agendamento"
                : authStep === AuthStep.VET_PET_SELECT
                  ? "Selecione o Pet"
                  : "Cadastro"}
            </Typography>
            <IconButton
              onClick={onClose}
              size="small"
              sx={{
                color: "text.secondary",
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent>
          <Box sx={{ mt: 1 }}>
            {renderContent()}
            {/* Container para reCAPTCHA do Firebase */}
            <div id="recaptcha-container"></div>
          </Box>
        </DialogContent>

        {authStep === AuthStep.CPF_INPUT && (
          <DialogActions>
            <Button onClick={onClose} color="inherit">
              Cancelar
            </Button>
          </DialogActions>
        )}
      </Dialog>

      {/* Dialog de verificação de token */}
      {renderTokenDialog()}
    </>
  );
}
